import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  Checks,
  DotsThreeVertical,
  List,
  MagnifyingGlass,
  Prohibit,
  Receipt,
  Timer,
  Truck,
  X,
} from "@phosphor-icons/react"
import { openConfirmDialog } from "@vesatogo/grass-confirm-modal"
import {
  Avatar,
  AvatarGroup,
  Button,
  Checkbox,
  CodenameTag,
  InputGroup,
  Menu,
  MenuItem,
  Popover,
  SegmentedControl,
  Spinner,
  Tag,
  Toast,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import dayjs from "dayjs"
import { merge, pick } from "lodash-es"
import { useEffect, useMemo, useState } from "react"
import { toast } from "react-hot-toast"
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { UseQueryArgs } from "urql"
import { useAuthorization } from "~/components/AuthorizationProvider"
import InvoiceActions from "~/components/InvoiceActions"
import { IssuanceNoteModal } from "~/components/IssuanceNoteModal"
import PreviewInvoice from "~/components/PreviewInvoice"
import Codenames from "~/constants/Codenames"
import FilterTypes from "~/constants/FilterTypes"
import { TradeKinds } from "~/constants/Kinds"
import { Permission } from "~/constants/Permissions"
import { siteConfig } from "~/constants/config"
import { OrdersCustom } from "~/constants/icons"
import { APIRoutes, AppRoutes } from "~/constants/routes"
import { TwilioMessages } from "~/constants/static.items"
import {
  AllInvoicesQueryVariables,
  Invoice_Bool_Exp,
  Order_By,
  UsersQuery,
  useAllInvoiceIssuanceNotesQuery,
  useAllInvoiceStatusesSubscription,
  useAllInvoicesQuery,
  useAuthenticatedUserQuery,
  useBulkUpdateInvoiceMutation,
} from "~/generated/graphql"
import InvoiceFilter from "~/pages/d/trade-requests/InvoiceFilter"
import UpdateTradeStatus from "~/pages/d/trade-requests/UpdateTradeStatus"
import { config } from "~/pages/pavti/config"
import { useInvoiceSelectorList } from "~/store/invoice-selector.store"
import { downloadPdf } from "~/utils/downloadPdf"
export function downloadInvoice(id: string | number) {
  return () => {
    const token = localStorage.getItem("token")
    downloadPdf(APIRoutes.downloadInvoice(id), token).catch(err => {
      return toast.custom(
        <Toast title={"Unable to download the invoice"} intent="danger" />
      )
    })
  }
}

const COMMON_FIELDS = [
  "id",
  "current_status_id",
  "status_processing",
  "unit",
  "buyer",
  "total_amount",
  "total_quantity",
]
const COLUMNS = [
  {
    accessor: "id",
    Header({ data }) {
      const selectable = data?.filter(d => d.id)
      const { selected, selectAll } = useInvoiceSelectorList()
      const isSelectAll =
        Object.keys(selected)?.length &&
        Object.keys(selected)?.length === selectable.length
      return (
        <Checkbox
          labelClassName="!mb-4"
          checked={isSelectAll ? true : false}
          onChange={() => {
            selectAll(selectable?.map(data => pick(data, COMMON_FIELDS)))
          }}
        />
      )
    },
    Cell({ value, row }) {
      const { selected, setSelected } = useInvoiceSelectorList()
      if (!row?.original?.id) return null
      return (
        <Checkbox
          checked={selected[value] ? true : false}
          onChange={() => {
            setSelected(pick(row.original, COMMON_FIELDS))
          }}
        />
      )
    },
  },
  {
    Header: <div className="pl-3">Invoice No</div>,
    accessor: "name",
    Cell({ row: { original } }) {
      const [params, setParams] = useSearchParams()
      if (!original.id) return "--"
      return (
        <span
          onClick={() => {
            params.set("invoice", original.id)
            setParams(params)
          }}
          className="link-text cursor-pointer"
        >
          Invoice #{original.id}
        </span>
      )
    },
  },
  {
    Header: "Created On",
    accessor: "created_at",
    Cell({ value }) {
      if (!value) return "--"
      return (
        <Cell
          title={dayjs(value).format("D MMM 'YY")}
          subtitle={dayjs(value).format("hh:mm a")}
        />
      )
    },
  },
  {
    Header: "Suppliers",
    accessor: "trades",
    Cell({ value }) {
      if (!value?.length) return "--"
      return (
        <AvatarGroup>
          {value.map(value => {
            const user = value?.seller?.user?.[0]
            if (!user) return null
            const blockStyle = user.is_blocked
              ? {
                  border: "2px solid #F13434",
                  backgroundColor: "#ffe6e6",
                  color: "#F13434",
                }
              : undefined

            return (
              <Tooltip
                content={
                  <div>
                    <div>{user.full_name}</div>
                    <div>{user.external_reference_id}</div>
                  </div>
                }
                key={value.id}
              >
                <Avatar
                  style={blockStyle}
                  name={user.full_name}
                  src={user?.photo?.url}
                />
              </Tooltip>
            )
          })}
        </AvatarGroup>
      )
    },
  },
  {
    Header: "Buyer",
    accessor: "buyer.user[0]",
    Cell({ value, row: { original } }) {
      if (!value) return "--"
      const credit = original?.credit?.[0]?.id
      const creditVerified = original?.credit?.[0]?.is_verified
      const creditRejected = original?.credit?.[0]?.is_verified === false
      const debit = original?.debit?.[0]?.id
      const debitVerified = original?.debit?.[0]?.is_verified
      const debitRejected = original?.debit?.[0]?.is_verified === false
      return (
        <Cell
          title={
            <div className="flex items-center gap-1">
              {value.full_name}{" "}
              {value.is_blocked && (
                <Prohibit color="red" size={20} weight="fill" />
              )}
              {credit ? (
                <Tag
                  intent={
                    creditVerified
                      ? "primary"
                      : creditRejected
                      ? "danger"
                      : "warning"
                  }
                  className="!rounded-xl"
                  variant="outline"
                >
                  Credit
                </Tag>
              ) : null}
              {debit ? (
                <Tag
                  intent={
                    debitVerified
                      ? "primary"
                      : debitRejected
                      ? "danger"
                      : "warning"
                  }
                  className="!rounded-xl"
                  variant="outline"
                >
                  Debit
                </Tag>
              ) : null}
            </div>
          }
          subtitle={<CodenameTag referenceId={value.external_reference_id} />}
        />
      )
    },
  },
  {
    Header: "Total Quantity",
    accessor: "total_quantity",
    Cell({ value, row: { original } }) {
      if (!value && value !== 0) return "--"
      const total_returned = original?.trades?.reduce(
        (acc, trade) =>
          acc + trade?.trade_items_aggregate?.aggregate?.sum?.returned_quantity,
        0
      )
      return (
        <Cell
          title={
            <span>
              {value || "0"}{" "}
              <span className="text-gray-500">
                {original?.unit?.[0]?.trade_items?.[0]?.quantity_unit?.name ||
                  "na"}
              </span>
            </span>
          }
          subtitle={
            total_returned ? (
              <Tag
                className="!rounded-xl"
                intent="danger"
                variant="minimal"
              >{`Revised: ${total_returned}`}</Tag>
            ) : (
              ""
            )
          }
        />
      )
    },
  },
  {
    Header: "Total Amount",
    accessor: "buyer_bifurcation.deducted_amount",
    Cell({ value, row: { original } }) {
      if (!value) return "--"
      const gst = original?.buyer_bifurcation?.buyer_tax_amount?.val
      const expense = original?.buyer_bifurcation?.buyer_expense_amount?.val
      const expenseGst =
        original?.buyer_bifurcation?.buyer_expense_tax_amount?.val

      const total =
        Number(value?.val) + Number(gst) + Number(expense) + Number(expenseGst)
      return (
        <Cell
          titleClassName="font-700 !text-green"
          title={
            <Tooltip
              content={`Base: ${inr(value?.val)} | GST: ${inr(
                gst
              )} | Expense: ${inr(expense)} | Expense GST: ${inr(expenseGst)}`}
            >
              <div>{inr(total)}</div>
            </Tooltip>
          }
          subtitle={gst ? "Incl. of taxes" : null}
        />
      )
    },
  },
  {
    Header: "Msg Status",
    accessor: "twilio_message.status",
    Cell({ value }) {
      const twilioStatus = TwilioMessages[value]
      if (twilioStatus)
        return (
          <div className="flex gap-1">
            {twilioStatus.id === "failed" ? (
              <X size={20} weight="bold" color={twilioStatus.color} />
            ) : (
              <Checks size={20} weight="bold" color={twilioStatus.color} />
            )}
            {twilioStatus.name}
          </div>
        )
      if (value) return value
      return "--"
    },
  },
  {
    Header: "Placed By",
    accessor: "created_by",
    Cell({ value, row }) {
      if (!value) return "--"
      return (
        <Tooltip
          content={
            <div>
              <p>{value?.full_name || "na"}</p>
              <p className="text-gray-400">
                {dayjs(row?.original?.created_at).format("DD MMM 'YY | h:mm a")}
              </p>
            </div>
          }
        >
          <Avatar
            className="relative text-center"
            name={value?.name}
            src={value?.photo?.url}
          />
        </Tooltip>
      )
    },
  },
  {
    Header: "Status",
    accessor: "current_status_id",
    Cell({ row: { original }, value }) {
      return (
        <Cell
          title={
            value ? (
              <span className="flex gap-1 items-center">
                {value}{" "}
                {original?.status_processing && (
                  <Tooltip content="Status change in progress">
                    <Timer size={16} className="text-blue-300" weight="fill" />
                  </Tooltip>
                )}
              </span>
            ) : (
              "na"
            )
          }
          subtitle={dayjs(original.updated_at).format("D MMM 'YY | hh:mm A")}
        />
      )
    },
  },
  {
    Header: "",
    accessor: "updated_at",
    Cell({ row: { original } }) {
      const [params, setParams] = useSearchParams()
      const navigate = useNavigate()
      const [isShown, setShown] = useState(false)
      const currentStatus = original?.current_status_id
      const isInvoiceCancelled = currentStatus === Codenames.InvoiceCancelled
      const [{ data: tradeIssuanceNotesData, fetching }] =
        useAllInvoiceIssuanceNotesQuery({
          variables: {
            invoiceId: original.id,
          },
          pause: !isShown,
        })

      const isQuantityRevised = Boolean(
        tradeIssuanceNotesData?.invoice_by_pk?.quantity_revised?.aggregate
          ?.count
      )
      return (
        <Popover
          minimal
          arrow={false}
          className="p-0"
          trigger="click"
          interactive
          animation="fade"
          theme="light-border"
          placement="bottom"
          onShow={() => setShown(true)}
          content={
            <Menu className="max-h-52">
              <MenuItem onClick={downloadInvoice(original.id)}>
                Download Invoice
              </MenuItem>
              <MenuItem
                onClick={() => {
                  params.set("update", original.id)
                  navigate(
                    {
                      pathname: location.pathname,
                      search: `?${createSearchParams(params)}`,
                    },
                    {
                      state: { current_status_id: original.current_status_id },
                    }
                  )
                }}
              >
                Update Status
              </MenuItem>
              <div className="text-gray-800 font-600 p-2 underline">
                Buyer Invoice Adjustment
              </div>
              <MenuItem
                disabled={
                  isQuantityRevised ||
                  (original?.kind === TradeKinds.InwardPatti &&
                    original?.current_status_id !== Codenames.GenerateInward &&
                    original?.current_status_id !== Codenames.InwardConfirm) ||
                  (original?.kind === TradeKinds.HishobPatti &&
                    original?.current_status_id !== Codenames.GenerateSales) ||
                  isInvoiceCancelled
                }
                onClick={() => {
                  if (
                    tradeIssuanceNotesData?.invoice_by_pk?.buyer_credit_pending
                      ?.aggregate?.count
                  ) {
                    return openConfirmDialog({
                      isOpen: true,
                      message:
                        "Debit note in Pending state found! Please Approve/ Reject to add new.",
                      onSubmit: () => {
                        return
                      },
                      secondaryButtonProps: { hidden: true },
                    })
                  } else {
                    params.set("creditNote", "new")
                    params.set("invoiceId", original.id)
                    params.set("user_role", "buyer")
                    setParams(params)
                  }
                  return
                }}
              >
                Add Credit Note
              </MenuItem>
              <MenuItem
                disabled={
                  isQuantityRevised ||
                  (original?.kind === TradeKinds.InwardPatti &&
                    original?.current_status_id !== Codenames.GenerateInward &&
                    original?.current_status_id !== Codenames.InwardConfirm) ||
                  (original?.kind === TradeKinds.HishobPatti &&
                    original?.current_status_id !== Codenames.GenerateSales) ||
                  isInvoiceCancelled
                }
                onClick={() => {
                  if (
                    tradeIssuanceNotesData?.invoice_by_pk?.buyer_debit_pending
                      ?.aggregate?.count
                  ) {
                    return openConfirmDialog({
                      isOpen: true,
                      message:
                        "Debit note in Pending state found! Please Approve/ Reject to add new.",
                      onSubmit: () => {
                        return
                      },
                      secondaryButtonProps: { hidden: true },
                    })
                  } else {
                    params.set("debitNote", "new")
                    params.set("invoiceId", original.id)
                    params.set("user_role", "buyer")
                    setParams(params)
                  }

                  return
                }}
              >
                Add Debit Note
              </MenuItem>
              <hr />
              <MenuItem
                disabled={
                  !Boolean(
                    tradeIssuanceNotesData?.invoice_by_pk?.buyer_credit
                      ?.aggregate?.count
                  )
                }
                onClick={() => {
                  params.set("creditNote", "view")
                  params.set("invoiceId", original.id)
                  params.set("user_role", "buyer")
                  setParams(params)

                  return
                }}
              >
                View Credit Note{" "}
                {
                  <Tag
                    intent="info"
                    variant="outline"
                    className="!rounded-xl ml-2"
                  >
                    {fetching ? (
                      <Spinner size={"sm"} />
                    ) : (
                      tradeIssuanceNotesData?.invoice_by_pk?.buyer_credit
                        ?.aggregate?.count
                    )}
                  </Tag>
                }
              </MenuItem>
              <MenuItem
                disabled={
                  !Boolean(
                    tradeIssuanceNotesData?.invoice_by_pk?.buyer_debit
                      ?.aggregate?.count
                  )
                }
                onClick={() => {
                  params.set("debitNote", "view")
                  params.set("invoiceId", original.id)
                  params.set("user_role", "buyer")
                  setParams(params)

                  return
                }}
              >
                View Debit Note{" "}
                <Tag
                  intent="info"
                  variant="outline"
                  className="!rounded-xl ml-2"
                >
                  {fetching ? (
                    <Spinner size={"sm"} />
                  ) : (
                    tradeIssuanceNotesData?.invoice_by_pk?.buyer_debit
                      ?.aggregate?.count
                  )}
                </Tag>
              </MenuItem>
            </Menu>
          }
        >
          <button className="hover:bg-gray-300 rounded">
            <DotsThreeVertical size={20} />
          </button>
        </Popover>
      )
    },
  },
]

export type FilterType = {
  sb_seller?: UsersQuery["base_user"][0]
  sb_buyer?: UsersQuery["base_user"][0]
  oth_created_at?: any
}
const TradeInvoices = () => {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [{ data: aData }] = useAuthenticatedUserQuery()
  const search = params.get("search")
  const updatedAt = params.get("updatedAt")
  const page = Number(params.get("page")) || 1
  const [filter, setFilter] = useState<FilterType>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.invoiceFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.invoiceFilter, JSON.stringify(filter))
  }, [filter])

  const [debouncedSearch] = useDebouncedValue(search?.trim(), config.debounce)
  const canViewTrades = useAuthorization(Permission.ViewTrade)
  const canViewInvoices = useAuthorization(Permission.ViewInvoice)
  const canAddMultiCommodity = useAuthorization(
    Permission.CanAddMultiCommodityTradesAndReceipts
  )
  const buyerCode = filter.sb_buyer?.codename
  const sellerCode = filter.sb_seller?.codename
  const currentDeptCodename = aData?.me?.agent?.current_department?.codename
  const [department] = useLocalStorage({ key: "department" })
  const { selected, selectAll } = useInvoiceSelectorList()
  const [isMulti, setMulti] = useLocalStorage<boolean>({
    key: "invoiceIsMultiCommodity",
    defaultValue: false,
    getInitialValueInEffect: false,
  })

  const createdAt = filter?.oth_created_at
  const whereClause: Invoice_Bool_Exp = {
    ...(debouncedSearch
      ? Number(debouncedSearch)
        ? { id: { _eq: Number(debouncedSearch) } }
        : { tracking_code: { _ilike: `%${debouncedSearch}%` } }
      : {}),
    created_at: createdAt
      ? {
          _lte: dayjs(createdAt).endOf("day").toISOString(),
          _gte: dayjs(createdAt).startOf("day").toISOString(),
        }
      : undefined,
    trades: {
      is_multiple_commodity: {
        _eq: isMulti,
      },
      department: {
        reference_id: {
          _eq: (department && String(department)) || currentDeptCodename,
        },
      },
      buyer: {
        reference_id: {
          _eq: buyerCode,
        },
      },
      seller: {
        reference_id: {
          _eq: sellerCode,
        },
      },
    },
  }
  const invoiceVariables: Omit<
    UseQueryArgs<AllInvoicesQueryVariables>,
    "query"
  > = {
    variables: {
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      order_by: { created_at: Order_By.DescNullsLast },
      where: whereClause,
    },
    pause: !department && !currentDeptCodename,
    requestPolicy: "network-only",
  }
  const [{ data: iData, fetching }, refetch] =
    useAllInvoicesQuery(invoiceVariables)
  const [{ data: sData }] = useAllInvoiceStatusesSubscription(invoiceVariables)

  const data = useMemo(
    () => (iData?.invoice?.length ? merge(iData, sData) : undefined),
    [iData, sData]
  )

  const totalInvoices = data?.invoice_aggregate?.aggregate?.count || 0

  useEffect(() => {
    if (!canViewInvoices && currentDeptCodename) {
      navigate(AppRoutes.tradeReceipts)
    }
  }, [canViewInvoices, currentDeptCodename])
  useEffect(() => {
    if (updatedAt) {
      refetch()
      params.delete("updatedAt")
      setParams(params)
    }
  }, [updatedAt])
  return (
    <>
      <header className="grid grid-cols-3 justify-between p-2 border-b-1 border-b-gray-300">
        <SegmentedControl
          value={"invoices"}
          onChange={value => {
            if (value === "trades" && canViewTrades) {
              navigate(AppRoutes.tradeRequests)
            } else if (value === "receipts") {
              navigate(AppRoutes.tradeReceipts)
            } else if (value === "trip") {
              navigate(AppRoutes.tradeTrips)
            }
          }}
          data={[
            canViewTrades && {
              label: "Trades",
              value: "trades",
              icon: <List className="mr-1" />,
            },
            {
              label: "Receipts",
              value: "receipts",
              icon: <Receipt className="mr-1" />,
            },
            {
              label: "Invoices",
              value: "invoices",
              icon: <OrdersCustom className="mr-1" />,
            },
            {
              label: "Trip",
              value: "trip",
              icon: <Truck className="mr-1" />,
            },
          ].filter(b => b)}
        />
        <div className="flex items-center gap-3">
          <InputGroup
            className={"w-full"}
            inputProps={{
              value: search || "",
              placeholder: "Search by Invoice no.",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
            leftElement={<MagnifyingGlass />}
          />
          <InvoiceFilter filter={filter} setFilter={setFilter} />
        </div>
        <div className="flex gap-3 justify-end items-center">
          <div className="flex flex-col">
            {canAddMultiCommodity && (
              <Checkbox
                label="Is Multi Commodity"
                value={isMulti as any}
                checked={isMulti}
                onChange={() => setMulti(!isMulti)}
              />
            )}
          </div>
          <InvoiceActions />
          {!department && !currentDeptCodename ? null : (
            <Button
              onClick={() => {
                params.set("update", "all")
                setParams(params)
              }}
              variant="outline"
            >
              Update Invoice
            </Button>
          )}
        </div>
      </header>
      <TableView
        relative={false}
        className="!h-[calc(100vh-64px-57px)]"
        getRowProps={({ original }) => ({
          className:
            original.buyer?.user?.[0]?.is_blocked ||
            (Array.isArray(original?.trades) &&
              original?.trades.some(
                trade => trade?.seller?.user?.[0]?.is_blocked
              ))
              ? "bg-red-100 hover:bg-red-100"
              : "",
        })}
        paginationProps={{
          total: Math.ceil(totalInvoices / config.pageSize),
          page: page,
          onChange(page) {
            params.set("page", page.toString())
            params.set("search", "")
            setParams(params)
          },
        }}
        isLoading={fetching}
        columns={COLUMNS}
        data={data?.invoice || []}
        meta={<>{totalInvoices} invoices</>}
      />
      <UpdateTradeStatus
        entity="invoice"
        bulkUpdateHook={useBulkUpdateInvoiceMutation}
        storeListHook={useInvoiceSelectorList}
      />
      <PreviewInvoice />
      <IssuanceNoteModal
        type="CREDIT"
        onSubmit={() => {
          refetch({
            requestPolicy: "network-only",
          })
        }}
      />
      <IssuanceNoteModal
        type="DEBIT"
        onSubmit={() => {
          refetch({
            requestPolicy: "network-only",
          })
        }}
      />
    </>
  )
}

export default TradeInvoices
